import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Badge, Button, Modal, Tooltip } from "flowbite-react";
import { HiHome, HiEye, HiPencil } from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { DataGridPagination, QueryParams, Subcategory } from "@/sharedTypes";
import {
  useGetSubcategoryAgentsQuery,
  useGetSubcategoryByIdQuery,
} from "@/api/ticketApi/subcategory";
import DataTable from "@/components/Table";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "react-toastify";
import { useUpdateAgentSkillMutation, useAddSubcategorySkillsMutation } from "@/api/agentApi/agentSkill";
import { getErrorMessage } from "@/utils/requestUtils";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function SubcategoryDetails() {
  const { t } = useTranslation(["subcategory", "common"]);
  const user = useCurrentUser();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agentParams, setAgentParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<any>(null);
  const [ratingValue, setRatingValue] = useState(0);

  const {
    data: subcategory,
    isLoading: isSubcategoryLoading,
    isError: isSubcategoryError,
    error: subcategoryError,
  } = useGetSubcategoryByIdQuery(id || "", { skip: !id });

  const {
    data: agentsData,
    isLoading: isAgentsLoading,
    isError: isAgentsError,
    error: agentsError,
  } = useGetSubcategoryAgentsQuery(
    { subcategoryId: id || "", params: agentParams },
    { skip: !id }
  );
  
  const [updateAgentSkill, { isLoading: isUpdating }] = useUpdateAgentSkillMutation();
  const [createAgentSkill, { isLoading: isCreating }] = useAddSubcategorySkillsMutation();

  useEffect(() => {
    if (isSubcategoryError) {
      toast.error(getErrorMessage(subcategoryError));
    }
    if (isAgentsError) {
      toast.error(getErrorMessage(agentsError));
    }
  }, [isSubcategoryError, subcategoryError, isAgentsError, agentsError]);

  const agentPagination: DataGridPagination = useMemo(() => {
    const pagination = agentsData?.pagination || {};
    return {
      pageIndex: pagination.pageIndex ?? pagination.page ?? agentParams.pagination.page,
      pageSize: pagination.pageSize ?? agentParams.pagination.pageSize,
      totalPages: pagination.totalPages ?? 1,
      totalItems: pagination.totalItems ?? pagination.total ?? 0,
    };
  }, [agentsData, agentParams.pagination.page, agentParams.pagination.pageSize]);

  const handleAgentPageChange = (page: number) => {
    setAgentParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const getSkillData = (row: any) => {
    // Check for nested agent_skills array first (common in some API responses)
    if (row.agent_skills && Array.isArray(row.agent_skills) && row.agent_skills.length > 0) {
      const skill = row.agent_skills[0];
      return { 
        skill_rate: skill.skill_rate, 
        id: skill.id || skill.agent_skill_id 
      };
    }
    // Check for flat structure
    if (row.skill_rate !== undefined) {
      return { 
        skill_rate: row.skill_rate, 
        id: row.agent_skill_id || row.id 
      };
    }
    return { skill_rate: 0, id: null };
  };

  const openRatingModal = (row: any) => {
    const { skill_rate } = getSkillData(row);
    setEditingAgent(row);
    setRatingValue(skill_rate || 0);
    setIsEditModalOpen(true);
  };

  const handleUpdateRating = async () => {
    if (!editingAgent) return;
    try {
      const { id: skillId } = getSkillData(editingAgent);
      
      if (skillId) {
        // Update existing skill
        await updateAgentSkill({
          id: skillId,
          data: {
            subcategory_id: Number(id),
            skill_rate: ratingValue,
          },
        }).unwrap();
      } else {
        // Create new skill
        const agentId = editingAgent.agent_id || editingAgent.agent?.agent_id;
        if (!agentId) {
           toast.error("Agent ID missing");
           return;
        }

        await createAgentSkill({
          agent_id: agentId,
          skills: [{
             subcategory_id: Number(id),
             skill_rate: ratingValue
          }]
        }).unwrap();
      }
      
      toast.success(t("common:success"));
      setIsEditModalOpen(false);
      setEditingAgent(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const subcategoryDetails = subcategory as Subcategory;

  const agentColumns = [
    {
      key: "agent",
      label: t("subcategory:agent"),
      render: (row: any) => (
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {row.agent_username || row.agent?.agent_username || "—"}
          </p>
          <p className="text-xs text-gray-500">
            {row.agent_email || row.agent?.agent_email || ""}
          </p>
        </div>
      ),
    },
    {
      key: "skill_rate",
      label: t("subcategory:skill_rate"),
      render: (row: any) => {
        const { skill_rate } = getSkillData(row);
        const rating = skill_rate || 0;
        return (
          <div className="flex text-yellow-400">
             {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className="text-lg">
                  {star <= rating ? "★" : "☆"}
                </span>
             ))}
          </div>
        );
      },
    },
    {
      key: "actions",
      label: t("subcategory:actions"),
      render: (row: any) => {
        const agentId = row.agent_id ?? row.agent?.agent_id;

        if (!agentId) return <span className="text-sm text-gray-400">—</span>;
        
        return (
          <div className="flex gap-2">
            <Tooltip content={t("common:view")}>
              <Button
                size="xs"
                color="light"
                onClick={() => navigate(`/agents/${agentId}`)}
              >
                <HiEye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </Button>
            </Tooltip>
            {/* Edit/Rate Button */}
            {user?.agent_role === agentRoles.company_admin && (
              <Tooltip content={t("common:edit")}>
                <Button
                  size="xs"
                  color="light"
                  onClick={() => openRatingModal(row)}
                >
                  <HiPencil className="h-4 w-4 text-green-600 dark:text-green-400" />
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <NavbarSidebarLayout>
      {isSubcategoryLoading ? <LoadingPage /> : !subcategory ? <div className="p-4">{t("subcategory:not_found")}</div> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/subcategories">{t("subcategory:subcategories_title")}</Breadcrumb.Item>
              <Breadcrumb.Item>{subcategoryDetails.subcategory_name}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                    {subcategoryDetails.subcategory_name}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {subcategoryDetails.subcategory_description || t("subcategory:no_description")}
                  </p>
                </div>
                <Badge size="md" color={subcategoryDetails.is_active ? "success" : "failure"}>
                  {subcategoryDetails.is_active ? t("subcategory:active") : t("subcategory:inactive")}
                </Badge>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("subcategory:agents_with_subcategory")}
              </h3>
              {isAgentsLoading ? (
                <LoadingPage />
              ) : (
                <DataTable
                  columns={agentColumns}
                  data={agentsData?.items || []}
                  pagination={agentPagination}
                  onPageChange={handleAgentPageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>}

      <Modal show={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <Modal.Header>
           {t("subcategory:update_skill_rate")} - {editingAgent?.agent_username || editingAgent?.agent?.agent_username}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col items-center gap-4">
            <p className="text-gray-500 dark:text-gray-400">
              {t("subcategory:select_rating_level")}
            </p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRatingValue(star)}
                  className={`text-3xl focus:outline-none transition-colors ${
                    star <= ratingValue ? "text-yellow-400" : "text-gray-300 dark:text-gray-600"
                  }`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setIsEditModalOpen(false)}>
            {t("common:cancel")}
          </Button>
          <Button color="blue" onClick={handleUpdateRating} isProcessing={isUpdating}>
            {t("common:save")}
          </Button>
        </Modal.Footer>
      </Modal>

    </NavbarSidebarLayout>
  );
}
